import { FC, useCallback, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { UserContext } from '../App'
import ContentWithHeader from '../components/shared/ContentWithHeader'
import { useToast } from '../components/shared/ToastProvider'
import useQueryParams from '../components/shared/useQueryParams'
import TransactionForm, { TransactionType } from '../components/transactions/TransactionForm'
import TransactionTypeChoice from '../components/transactions/TransactionTypeChoice'
import { TransactionUpdatableFields } from '../domain/model/transaction'
import { TransactionServiceContext } from '../service/ServiceContext'

type QueryParams = {
  type: string
}

const CreateTransactionPage: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationState = location.state as {
    from?: string
    prefillAccountId?: number | null
    prefillTransactionGroupId?: number | null
  } | null
  const returnTo = navigationState?.from ?? '/transactions'
  const prefillAccountId = navigationState?.prefillAccountId ?? null
  const prefillTransactionGroupId = navigationState?.prefillTransactionGroupId ?? null
  const { queryParams } = useQueryParams<QueryParams>()
  const type = queryParams.type as TransactionType | undefined

  const { create: createTransaction } = useContext(TransactionServiceContext)
  const { email } = useContext(UserContext)
  const { showToast } = useToast()

  const onSubmit = useCallback(
    async (data: Partial<TransactionUpdatableFields>) => {
      if (typeof data.amount === 'undefined') throw new Error('amount cannot be undefined')
      if (typeof data.currencyId === 'undefined') throw new Error('currencyId cannot be undefined')
      if (typeof data.categoryId === 'undefined') throw new Error('categoryId cannot be undefined')
      if (typeof data.date === 'undefined') throw new Error('date cannot be undefined')
      if (typeof data.senderId === 'undefined') throw new Error('senderId cannot be undefined')
      if (typeof data.receiverId === 'undefined') throw new Error('receiverId cannot be undefined')
      if (typeof data.note === 'undefined') throw new Error('note cannot be undefined')
      if (typeof data.receiverCurrencyId === 'undefined') throw new Error('receiverCurrencyId cannot be undefined')
      if (typeof data.receiverAmount === 'undefined') throw new Error('receiverAmount cannot be undefined')

      await createTransaction({
        owner: email,
        amount: data.amount,
        currencyId: data.currencyId,
        categoryId: data.categoryId,
        date: data.date,
        senderId: data.senderId,
        receiverId: data.receiverId,
        note: data.note,
        receiverCurrencyId: data.receiverCurrencyId,
        receiverAmount: data.receiverAmount,
        financialIncomeData: data.financialIncomeData ?? null,
        transactionGroupData: data.transactionGroupData ?? null,
      })
      showToast('Transaction recorded')
      navigate(returnTo, { replace: true })
    },
    [navigate, showToast, returnTo, createTransaction, email],
  )

  return (
    <ContentWithHeader
      title={`Record new ${type === 'financialIncome' ? 'financial income' : (type ?? 'transaction')}`}
      action="return"
      withPadding
      withScrolling
    >
      {type === undefined ? (
        <TransactionTypeChoice
          onChoose={(chosen) => navigate({ search: `?type=${chosen}` }, { state: location.state })}
        />
      ) : (
        <TransactionForm
          onSubmit={onSubmit}
          submitText="Record"
          type={type}
          prefillAccountId={prefillAccountId}
          prefillTransactionGroupId={prefillTransactionGroupId}
          groupContext={prefillTransactionGroupId !== null ? 'locked' : undefined}
          onTypeChange={(changed) => navigate({ search: `?type=${changed}` }, { replace: true, state: location.state })}
        />
      )}
    </ContentWithHeader>
  )
}

export default CreateTransactionPage
